import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthForm } from '../hooks/useAuthForm';
import { styles } from './AuthWrapper.styles';
import AuthShell from './auth/AuthShell';
import SignInForm from './auth/SignInForm';
import SignUpForm from './auth/SignUpForm';
import ConfirmForm from './auth/ConfirmForm';
import ResetRequestForm from './auth/ResetRequestForm';
import ResetConfirmForm from './auth/ResetConfirmForm';
import './AuthWrapper.css';

interface AuthWrapperProps {
  children?: React.ReactNode;
}

/**
 * Authentication gate. Fails closed: while loading it shows a spinner, and an
 * unauthenticated user hitting a protected route is redirected to /login and
 * renders nothing until navigation completes. Authenticated users get their
 * content (or the routed <Outlet />). Unauthenticated visitors of /login see
 * the auth screen, whose state + handlers live in useAuthForm and whose UI is
 * composed from the focused components under ./auth.
 */
const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const form = useAuthForm();

  // Redirect unauthenticated users accessing protected routes.
  useEffect(() => {
    if (!isAuthenticated && children && !isLoading) {
      const currentPath = location.pathname;
      navigate(`/login?next=${encodeURIComponent(currentPath)}`, { replace: true });
    }
  }, [isAuthenticated, children, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // Unauthenticated user on a protected route: render nothing; the effect navigates.
  if (!isAuthenticated && children) {
    return null;
  }

  if (isAuthenticated) {
    return <>{children ?? <Outlet />}</>;
  }

  return (
    <AuthShell form={form}>
      {form.authMode === 'signin' && <SignInForm form={form} />}
      {form.authMode === 'signup' && <SignUpForm form={form} />}
      {form.authMode === 'confirm' && <ConfirmForm form={form} />}
      {form.authMode === 'resetRequest' && <ResetRequestForm form={form} />}
      {form.authMode === 'resetConfirm' && <ResetConfirmForm form={form} />}
    </AuthShell>
  );
};

export default AuthWrapper;
